(() => {
  const initializeReveals = () => {
    const reveals = document.querySelectorAll('[data-droid-reveal]:not(.is-visible)');
    if (!reveals.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      reveals.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: .12 });
    reveals.forEach((element) => observer.observe(element));
  };

  const initializeTextAnimations = () => {
    const selector = [
      '#MainContent h1:not([data-droid-text])',
      '#MainContent h2:not([data-droid-text])',
      '#MainContent h3:not([data-droid-text])',
      '#MainContent .droid-eyebrow:not([data-droid-text])',
      '#MainContent .droid-page-kicker:not([data-droid-text])'
    ].join(',');
    const candidates = Array.from(document.querySelectorAll(selector)).filter((element) => {
      return !element.closest('[data-hero-slide], .droid-fashion-slide, [hidden], dialog');
    });
    if (!candidates.length) return;

    candidates.forEach((element, index) => {
      element.setAttribute('data-droid-text', '');
      element.style.setProperty('--droid-text-delay', `${(index % 4) * 70}ms`);
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      candidates.forEach((element) => element.classList.add('is-text-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-text-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: .16, rootMargin: '0px 0px -5% 0px' });
    candidates.forEach((element) => observer.observe(element));
  };

  const initializeChoices = (root) => {
    root.querySelectorAll('[data-droid-choice-group]').forEach((group) => {
      const buttons = Array.from(group.querySelectorAll('[data-droid-choice]'));
      const input = group.querySelector('[data-droid-choice-input]');
      buttons.forEach((button) => button.addEventListener('click', () => {
        buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        if (input) input.value = button.dataset.droidChoice;
        root.dispatchEvent(new CustomEvent('droid:choice', { detail: { value: button.dataset.droidChoice } }));
      }));
    });
  };

  const initializeServiceForm = (form) => {
    if (form.dataset.initialized === 'true') return;
    form.dataset.initialized = 'true';
    const status = form.querySelector('[data-droid-form-status]');
    const fileInput = form.querySelector('input[type="file"]');
    const colorRange = form.querySelector('[data-droid-color-range]');
    const colorOutput = form.querySelector('[data-droid-color-output]');
    const total = form.querySelector('[data-droid-total]');
    const totalNote = form.querySelector('[data-droid-total-note]');
    const typeInput = form.querySelector('[data-droid-choice-input]');
    const rangeWrap = form.querySelector('[data-droid-range-wrap]');
    const unitPrice = Number(form.dataset.unitPrice || 0) / 100;

    const updateTotal = () => {
      const fullColor = typeInput && typeInput.value === 'full-color';
      const quantity = fullColor && colorRange ? Number(colorRange.value) : 1;
      if (colorOutput) colorOutput.textContent = String(quantity);
      if (rangeWrap) rangeWrap.hidden = !fullColor;
      if (total && unitPrice) total.textContent = new Intl.NumberFormat(undefined, {style: 'currency', currency: form.dataset.currency || 'USD'}).format(unitPrice * quantity);
      if (totalNote) totalNote.textContent = `${quantity} ${quantity === 1 ? 'color' : 'colors'} × ${new Intl.NumberFormat(undefined, {style: 'currency', currency: form.dataset.currency || 'USD'}).format(unitPrice)}`;
    };

    if (colorRange) colorRange.addEventListener('input', updateTotal);
    form.addEventListener('droid:choice', updateTotal);
    updateTotal();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const variantId = form.dataset.variantId;
      if (!variantId) {
        status.textContent = 'Choose the Shopify service product in the theme editor before publishing.';
        return;
      }
      if (!fileInput || !fileInput.files.length) {
        status.textContent = 'Please choose an artwork file.';
        return;
      }
      const submit = form.querySelector('button[type="submit"]');
      submit.disabled = true;
      status.textContent = 'Securely uploading your artwork…';
      try {
        const uploadData = new FormData();
        uploadData.append('file', fileInput.files[0]);
        uploadData.append('service', form.dataset.service || 'artwork-service');
        const uploadResponse = await fetch(form.dataset.uploadEndpoint, { method: 'POST', body: uploadData, headers: { Accept: 'application/json' } });
        const upload = await uploadResponse.json();
        if (!uploadResponse.ok || !(upload.file_url || upload.url)) throw new Error(upload.message || 'The artwork upload service is not connected yet.');
        const fileUrl = upload.file_url || upload.url;
        const quantity = typeInput && typeInput.value === 'full-color' && colorRange ? Number(colorRange.value) : 1;
        const properties = {
          'Droid service': form.dataset.serviceLabel || form.dataset.service,
          'Artwork file': fileUrl,
          'Original filename': fileInput.files[0].name
        };
        new FormData(form).forEach((value, key) => {
          if (key !== 'file' && typeof value === 'string' && value.trim()) properties[key] = value;
        });
        const cartResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity, properties }] })
        });
        const cart = await cartResponse.json();
        if (!cartResponse.ok) throw new Error(cart.description || 'This service could not be added to the cart.');
        window.location.assign('/cart');
      } catch (error) {
        status.textContent = error.message || 'This service is temporarily unavailable.';
        submit.disabled = false;
      }
    });
  };

  const initializeToolJumps = () => {
    document.querySelectorAll('[data-droid-tool-jump]').forEach((link) => link.addEventListener('click', (event) => {
      const workspace = document.querySelector('[data-droid-ai-tools]');
      if (!workspace) return;
      event.preventDefault();
      const button = workspace.querySelector(`[data-droid-tool="${link.dataset.droidToolJump}"]`);
      if (button) button.click();
      workspace.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
  };

  const initialize = () => {
    initializeTextAnimations();
    initializeReveals();
    document.querySelectorAll('[data-droid-service-form]').forEach((form) => {
      initializeChoices(form);
      initializeServiceForm(form);
    });
    initializeToolJumps();
  };

  document.addEventListener('DOMContentLoaded', initialize);
  document.addEventListener('shopify:section:load', initialize);
})();
